import { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useArrowKeyIndex } from '../../../../common/hooks/keyboard/useArrowKeyIndex';
import { useEnterKeyHandler } from '../../../../common/hooks/keyboard/useEnterKeyHandler';
import { useEscapeKeyHandler } from '../../../../common/hooks/keyboard/useEscapeKeyHandler';
import { AutoCompleteDropdown, AutoCompleteResult } from './AutoComplete.styles';
import { AutoCompletePrediction } from './AutoCompletePrediction';
import { PredictionType } from './PredictionType';

interface Props<T> {
  value?: string;
  autoCompleteHook: (value?: string) => T[];
  onSelect: Dispatch<T>;
  cancel: () => void;
}

export const AutoCompleteBody = <T extends PredictionType>({
  value,
  onSelect,
  cancel,
  autoCompleteHook,
}: Props<T>) => {
  const { t } = useTranslation();
  const predictions: T[] = autoCompleteHook(value);

  const [selectedIndex, setSelectedIndex] = useArrowKeyIndex(predictions.length);

  useEnterKeyHandler(() => {
    const prediction = predictions[selectedIndex];
    if (prediction) onSelect(prediction);
  });

  useEscapeKeyHandler(cancel);

  return (
    <AutoCompleteDropdown>
      {predictions.length === 0 && !value && (
        <AutoCompleteResult>{t('field.autocomplete.emptyPlaceholderText')}</AutoCompleteResult>
      )}
      {predictions.map((prediction, index) => (
        <AutoCompletePrediction
          key={index}
          prediction={prediction}
          onChoose={onSelect}
          onHover={setSelectedIndex}
          isSelected={index === selectedIndex}
          index={index}
        />
      ))}
    </AutoCompleteDropdown>
  );
};
